import { PAYMENT_STATUSES, PAYMENT_TYPES } from 'constants/constants';
import { DynamicObjectType } from 'core/types/common';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from 'utils/formattedString';

const DATA_TIME = {
  AWAITING_CONFIRMATION: {
    value: 'createdAt',
    text: 'createdTime',
  },
  CONFIRMED: {
    value: 'confirmedAt',
    text: 'confirmedTime',
  },
  RETURNING: {
    value: 'returnedAt',
    text: 'returningTime',
  },
  PROCESSING: {
    value: 'processedAt',
    text: 'processingTime',
  },
  COMPLETED: {
    value: 'completedAt',
    text: 'completedTime',
  },
  CANCELLED: {
    value: 'cancelledAt',
    text: 'cancelledTime',
  },
};

export default function OrderSummary({ data }) {
  const { t } = useTranslation();

  const paymentStatus = useMemo(
    () =>
      PAYMENT_STATUSES.find(
        (e) =>
          e.value ===
          data?.newOrder?.payments?.filter((e) => e.type !== 'point')?.[0]
            ?.status,
      ),
    [data],
  );

  const getPaymentMethod = useCallback(
    (payments, paymentType) => {
      const OPTIONS = PAYMENT_TYPES.concat(
        { value: 'bank', text: 'Bank' },
        { value: 'other', text: 'Other' },
      );
      if (payments?.length) {
        return payments
          .filter((payment) => payment.type !== 'point')
          .map(
            (payment) =>
              t(OPTIONS.find((type) => type.value === payment.type)?.text) ||
              payment?.paymentDetail?.method,
          )
          .join(', ');
      }
      return t(OPTIONS.find((type) => type.value === paymentType)?.text);
    },
    [t],
  );

  const summaries = useMemo(() => {
    const statusKey = data?.status;
    const dateTime = DATA_TIME[statusKey];

    return [
      { text: t('ordersExchange.exchangeNo'), value: data?.exchangeNumber },
      dateTime
        ? {
            text: t(`ordersExchange.${dateTime.text}`),
            value: formatDateTime(data?.[dateTime.value] || data?.createdAt),
          }
        : {},
      {
        text: t('ordersExchange.orderType'),
        value: data?.exchangeType,
      },
      {
        text: t('ordersExchange.paymentMethod'),
        value: getPaymentMethod(data?.newOrder?.payments, data?.paymentType),
      },
      {
        text: t('orderDetail.paymentStatus'),
        value: t(paymentStatus?.text),
        color: paymentStatus?.color,
      },
      { text: t('ordersExchange.channel'), value: data?.newOrder?.channel },
    ];
  }, [data, t, paymentStatus]);

  return (
    <div className="p-[10px] border-[1px] border-border-dark-gray bg-white">
      <div className="bg-bg-ghost-white p-[10px] flex gap-[6px]">
        {summaries
          ?.filter((i: DynamicObjectType) => !!i?.text)
          ?.map((item, index) => (
            <div
              key={`summary-${index.toString()}`}
              className="text-text-primary flex-1"
            >
              {item?.text}
              <div
                className="font-bold mt-[5px]"
                style={{
                  color: item?.color,
                }}
              >
                {item?.value}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
